import {
  renderSelectedOption,
  createPlaceholderHtml,
  createOptionsListHtml,
  createButtonHtml,
  removeButtonHtml
} from './templates';
import { updateAsyncTaskGanttV2 } from '../../GanttVisualization.helper';
import { hexToRgb } from '../../../../utils';

export const UI_ELEMENTS = {
  EMPTY_SELECTION: `<div class="empty-selection">
         <div class="plus-icon-container"></div>
       </div>`,
  INDEX_DESCRIPTION: 2
};

const initializeTagCallbacks = () => {
  window.tagCallbacks = window.tagCallbacks || {};
};

const updateTagsUI = (options) => {
  const { taskId, columnName, tags, column, optionsList } = options;
  const placeholderId = `tags-placeholder-${columnName}-${taskId}`;
  const placeholderEl = document.getElementById(placeholderId);

  if (placeholderEl) {
    requestAnimationFrame(() => {
      placeholderEl.innerHTML =
        tags.length > 0
          ? renderSelectedOption(tags, column)
          : UI_ELEMENTS.EMPTY_SELECTION;
    });
  }

  const optionsListEl = document.querySelector(
    `[data-task-id="${taskId}"] .options-list`
  );

  if (optionsListEl && optionsList) {
    const createBtn = optionsListEl.querySelector(
      '[data-action="tagCreate"]'
    )?.parentElement;
    const removeBtn = optionsListEl.querySelector(
      '[data-action="tagRemove"]'
    )?.parentElement;

    const newOptionsListHtml = createOptionsListHtml(optionsList, tags);

    optionsListEl.innerHTML = '';
    if (createBtn) optionsListEl.appendChild(createBtn);

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = newOptionsListHtml;
    while (tempDiv.firstChild) {
      optionsListEl.appendChild(tempDiv.firstChild);
    }

    if (removeBtn) optionsListEl.appendChild(removeBtn);
  }
};

const handleTagSelection = (task, newTag, options) => {
  const { taskFromLookahead, column, gantt, callbackOnSelection } = options;

  if (!Array.isArray(task.tags)) {
    task.tags = [];
  }

  const tagIndex = task.tags.findIndex(
    (tag) => Number(tag.id) === Number(newTag.id)
  );

  if (tagIndex === -1) {
    newTag.id = Number(newTag.id);
    task.tags.push(newTag);
  } else {
    task.tags.splice(tagIndex, 1);
  }

  updateTagsUI({
    taskId: task.id,
    columnName: column.name,
    tags: task.tags,
    column,
    optionsList: options.optionsList
  });

  if (callbackOnSelection) {
    const selectedIds = task.tags.map((tag) => Number(tag.id));
    const dictionary = { [Number(newTag.id)]: newTag };
    callbackOnSelection(selectedIds, dictionary);
  }

  if (taskFromLookahead) {
    updateAsyncTaskGanttV2(
      taskFromLookahead,
      column.name,
      task.tags,
      gantt,
      true,
      false,
      false,
      true,
      true
    );
  }
};

export const registerTaskCallbacks = (taskId, task, options) => {
  const { column, callbackOnClickCreate, callbackOnClickRemove } = options;

  initializeTagCallbacks();

  window.tagCallbacks[taskId] = {
    selection: (payload, dictionary) => {
      const newTag = dictionary[payload[0]];
      handleTagSelection(task, newTag, options);
    },

    create: () => callbackOnClickCreate && callbackOnClickCreate(),

    remove: () => {
      task.tags = [];
      updateTagsUI({
        taskId: task.id,
        columnName: column.name,
        tags: task.tags,
        column,
        optionsList: options.optionsList
      });
      if (callbackOnClickRemove) callbackOnClickRemove();
    }
  };
};

export const prepareUIElements = (options) => {
  const { selectedObject, optionsList, t, column, taskId } = options;
  const selectedTags = selectedObject[column.name] || selectedObject.tags || [];
  const objectId = taskId || selectedObject.id || 'default';

  const placeholderHtml = createPlaceholderHtml(
    column.name,
    objectId,
    selectedTags,
    column
  );
  const optionsListHtml = createOptionsListHtml(optionsList, selectedTags);
  const createBtnHtml = createButtonHtml(t);
  const removeBtnHtml = removeButtonHtml(t);

  return { placeholderHtml, optionsListHtml, createBtnHtml, removeBtnHtml };
};

const parseTagValue = (value) => {
  if (!value) return null;

  const parts = value.split('-');
  const valueId = parts[0];
  const valueName = parts[1];
  const valueDescription = parts.slice(UI_ELEMENTS.INDEX_DESCRIPTION).join('-');

  return {
    id: valueId,
    name: valueName,
    description: valueDescription
  };
};

const createTagDictionary = (tagData) => {
  if (!tagData) return {};

  const dictionary = {};
  dictionary[tagData.id] = tagData;
  return dictionary;
};

const handleTagsAction = (action, target, taskId) => {
  const callbacks = window.tagCallbacks && window.tagCallbacks[taskId];
  if (!callbacks) return;

  const actionHandlers = {
    tagSelection: () => {
      const value = target.getAttribute('data-value');
      const tagData = parseTagValue(value);

      if (!tagData) return;

      const listItem = target.closest('.dropdown-option');
      if (listItem) {
        listItem.classList.toggle('selected-option');
      }

      const dictionary = createTagDictionary(tagData);

      if (callbacks.selection) {
        callbacks.selection([tagData.id], dictionary);
      }
    },
    tagCreate: () => callbacks.create && callbacks.create(),
    tagRemove: () => callbacks.remove && callbacks.remove()
  };

  const handler = actionHandlers[action];
  if (handler) handler();
};

const createTagsEventHandler = () => (event) => {
  const target = event.target.closest('[data-action]');
  if (!target) return;

  const action = target.getAttribute('data-action');
  const container = target.closest('[data-task-id]');
  if (!container) return;

  const taskId = container.getAttribute('data-task-id');
  handleTagsAction(action, target, taskId);
  event.stopPropagation();
};

export const setupGlobalHandlers = () => {
  initializeTagCallbacks();

  if (window._tagsGlobalHandler) return;

  const tagsGlobalHandler = createTagsEventHandler();
  document.addEventListener('click', tagsGlobalHandler);
  window._tagsGlobalHandler = tagsGlobalHandler;
};

export const cleanupTagsGlobalHandlers = () => {
  if (window._tagsGlobalHandler) {
    document.removeEventListener('click', window._tagsGlobalHandler);
    delete window._tagsGlobalHandler;
  }
};

export const formatTagsToString = (tags, defaultValue = '') => {
  if (!tags || !Array.isArray(tags) || tags.length === 0) {
    return defaultValue;
  }

  const tagNames = tags
    .map((tag) => tag.name || tag.label || tag)
    .filter(Boolean);

  return tagNames.length > 0 ? tagNames.join(', ') : defaultValue;
};

export const updateTaskBarStyle = (taskBar, selectedObject, gantt, taskId) => {
  if (!taskBar) return;
  if (gantt.visualizationColorActive !== 'tags') {
    return;
  }

  const task = gantt.getTask(taskId);
  const ALPHA = 0.3;
  let color = '#b3b3b3';

  if (selectedObject.tags?.length > 0) {
    const firstTagData = selectedObject.tags[0];
    if (firstTagData?.description) {
      color = firstTagData.description;
    }
  }

  const rgbColor = hexToRgb(color, ALPHA);
  const backgroundColor = `rgba(${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b}, ${rgbColor.a})`;

  taskBar.style.setProperty('background-color', backgroundColor, 'important');
  taskBar.style.setProperty('border-color', color, 'important');

  task.color = backgroundColor;
  task.progressSolidColor = color;
  gantt.defineColorByTags(task);
};

export const setupMutationObserver = (
  taskId,
  columnName,
  selectedObject,
  gantt
) => {
  setTimeout(() => {
    const placeholderEl = document.getElementById(
      `tags-placeholder-container-${columnName}-${taskId}`
    );

    if (!placeholderEl) {
      return;
    }

    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === 'childList' ||
          mutation.type === 'characterData'
        ) {
          const task = gantt.getTask(taskId);
          let taskBar = null;

          if (task.is_parent) {
            taskBar = document.querySelector(
              `[data-task-id="${taskId}"].parent-task-style`
            );
          } else {
            taskBar = document.querySelector(
              `[data-task-id="${taskId}"].activitytask-bar-custom-style`
            );
          }

          updateTaskBarStyle(taskBar, selectedObject, gantt, taskId);
          break;
        }
      }
    });

    observer.observe(placeholderEl, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }, 0);
};
